import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Calendar, 
  Sparkles, 
  Download, 
  Share2, 
  RefreshCw,
  Cross,
  Star,
  Heart
} from 'lucide-react';
import CompositeImageGenerator from './CompositeImageGenerator';
import SaintHistoryCard from './SaintHistoryCard';

export const ResultsSection = ({ result, onNewSearch }) => {
  const { t, language } = useLanguage();

  if (!result) return null;

  const { 
    saintName, 
    feastDay, 
    archangel, 
    history, 
    // New detailed fields
    introduction,
    epoch,
    location,
    origin,
    lifeStory,
    symbolism,
    // Generation states
    prayer, 
    saintImage, 
    archangelImage,
    isGeneratingSaintImage,
    isGeneratingArchangelImage,
    isGeneratingPrayer
  } = result;

  return (
    <section id="results" className="py-20 bg-gradient-to-b from-background to-secondary/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-4">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold/50" />
              <Star className="w-6 h-6 text-gold" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold/50" />
            </div>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mb-2">
            {t('results.yourSaint')}
          </h2>
          <p className="text-gold font-heading text-2xl sm:text-3xl font-medium">
            {saintName}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Saint Image Card */}
          <Card className="card-divine overflow-hidden animate-fade-in-up">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Cross className="w-5 h-5 text-gold" />
                {language === 'fr' ? 'Portrait du Saint' : 'Saint Portrait'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted relative">
                {isGeneratingSaintImage ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-card">
                    <div className="spinner-sacred mb-4" />
                    <p className="text-sm text-muted-foreground">{t('results.generating')}</p>
                  </div>
                ) : saintImage ? (
                  <img 
                    src={saintImage} 
                    alt={saintName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
                    <Cross className="w-16 h-16 text-gold/30" />
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{t('results.feastDay')}: <strong className="text-foreground">{feastDay}</strong></span>
                </div>
                {saintImage && (
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('common.download')}</span>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Archangel Card */}
          <Card className="card-divine overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold" />
                {t('results.archangel')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted relative">
                {isGeneratingArchangelImage ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-card">
                    <div className="spinner-sacred mb-4" />
                    <p className="text-sm text-muted-foreground">{t('results.generating')}</p>
                  </div>
                ) : archangelImage ? (
                  <img 
                    src={archangelImage} 
                    alt={archangel}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
                    <Sparkles className="w-16 h-16 text-gold/30" />
                  </div>
                )}
              </div>
              <div className="mt-4">
                <p className="text-lg font-heading font-medium text-foreground">{archangel}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {language === 'fr' 
                    ? 'Votre ange gardien céleste' 
                    : 'Your heavenly guardian angel'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Prayer Card */}
        <Card className="card-divine mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-accent" />
              {t('results.prayer')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isGeneratingPrayer ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="spinner-sacred mb-4" />
                <p className="text-sm text-muted-foreground">{t('results.generatingPrayer')}</p>
              </div>
            ) : prayer ? (
              <div className="relative">
                <div className="absolute -top-2 -left-2 text-6xl text-gold/20 font-heading">"</div>
                <blockquote className="prayer-text text-lg text-foreground/90 pl-8 pr-4 py-4 border-l-4 border-gold/30 bg-secondary/30 rounded-r-lg">
                  {prayer}
                </blockquote>
                <div className="absolute -bottom-4 right-4 text-6xl text-gold/20 font-heading">"</div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {language === 'fr' ? 'Prière en cours de génération...' : 'Prayer being generated...'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* History Card - New detailed component */}
        <SaintHistoryCard
          saintName={saintName}
          introduction={introduction}
          epoch={epoch}
          location={location}
          origin={origin}
          lifeStory={lifeStory}
          symbolism={symbolism}
          feastDay={feastDay}
          history={history}
        />

        {/* Composite Image Generator */}
        {prayer && (
          <CompositeImageGenerator
            saintName={saintName}
            archangelName={archangel}
            prayer={prayer}
            feastDay={feastDay}
            introduction={introduction}
            epoch={epoch}
            location={location}
            origin={origin}
            lifeStory={lifeStory}
            symbolism={symbolism}
            saintImage={saintImage}
            archangelImage={archangelImage}
          />
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Button variant="sacred" size="lg" onClick={onNewSearch} className="gap-2">
            <RefreshCw className="w-5 h-5" />
            {language === 'fr' ? 'Nouvelle recherche' : 'New Search'}
          </Button>
          <Button variant="divine" size="lg" className="gap-2">
            <Share2 className="w-5 h-5" />
            {t('common.share')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
